import {Component, Input} from '@angular/core';
import {
  AttachmentForTechDto,
  AttachmentTypeForTech,
  AttachmentTypes,
  FileAttachmentForTechDto,
  FileOrgaAttachmentDto,
  FolderOrgaAttachmentDto,
  KnowledgeArticleOrgaAttachmentDto,
  LinkOrgaAttachmentDto,
  OrgaAttachmentDto,
  OrgaAttachmentListDto
} from "@server-models";

import {IonicModule} from "@ionic/angular";

import {EDesignType} from "@shared/components/attachments/enums/design-type.enum";
import {
  SharedAttachmentsRowItemComponent
} from "@shared/components/attachments/components/row/shared-attachments-row-item.component";
import {
  SharedAttachmentsBoxComponent
} from '@shared/components/attachments/components/box/shared-attachments-box-item.component';
import {ILocalAttachmentDto} from "@shared/components/attachments/interfaces/orga-attachment-dto-extend.interface";

@Component({
  selector: 'app-shared-attachments',
  templateUrl: './shared-attachments.component.html',
  styleUrls: ['./shared-attachments.component.scss'],
  imports: [
    IonicModule,
    SharedAttachmentsRowItemComponent,
    SharedAttachmentsBoxComponent
],
  standalone: true
})
export class SharedAttachmentsComponent {

  @Input() hasHeader: boolean;
  @Input() designType?: EDesignType;
  @Input() hasIconFolder: boolean;
  @Input() lines: string = 'full'

  mappedAttachments: ILocalAttachmentDto[];

  protected readonly DesignTypeEnum = EDesignType;

  @Input()
  set attachmentsTech(attachments: AttachmentForTechDto[] | null | undefined) {
    this.mappedAttachments = attachments?.flatMap(attachment => this._mapTechAttachment(attachment)) ?? [];
  }

  @Input()
  set attachmentsOrga(attachments: OrgaAttachmentDto[] | null | undefined) {
    this.mappedAttachments = attachments?.flatMap(attachment => this._mapOrgaAttachment(attachment)) ?? [];
  }

  @Input()
  set attachmentsOrgaList(attachments: OrgaAttachmentListDto[] | null | undefined) {
    this.mappedAttachments = attachments?.flatMap(attachment => this._mapOrgaListAttachment(attachment)) ?? [];
  }

  constructor() {
    this.mappedAttachments = [];
    this.hasHeader = true;
    this.hasIconFolder = false;
    this.designType = EDesignType.Row
  }

  private _mapTechAttachment(attachment: AttachmentForTechDto): ILocalAttachmentDto[] {
    switch (attachment.type) {
      case AttachmentTypeForTech.File: {
        const fileAttachment = attachment as FileAttachmentForTechDto;
        const fileId = fileAttachment.file?.fileId;
        return [{
          attachmentId: fileAttachment.attachmentId,
          fileId,
          loadingId: this._getLoadingId(fileAttachment.attachmentId, fileId),
          tenantId: fileAttachment.tenantId,
          name: fileAttachment.file?.name ?? undefined,
          url: fileAttachment.file?.uri ?? undefined,
          type: AttachmentTypes.File,
          mimeType: fileAttachment.file?.mimeType ?? undefined
        }];
      }
      default:
        return [];
    }
  }

  private _mapOrgaAttachment(attachment: OrgaAttachmentDto): ILocalAttachmentDto[] {
    switch (attachment.type) {
      case AttachmentTypes.File: {
        const fileAttachment = attachment as FileOrgaAttachmentDto;
        const fileId = fileAttachment.fileId ?? fileAttachment.file?.fileId;
        return [{
          attachmentId: fileAttachment.attachmentId,
          fileId,
          loadingId: this._getLoadingId(fileAttachment.attachmentId, fileId),
          tenantId: fileAttachment.tenantId,
          name: fileAttachment.file?.name ?? undefined,
          url: fileAttachment.file?.uri ?? undefined,
          type: AttachmentTypes.File,
          mimeType: fileAttachment.file?.mimeType ?? undefined
        }];
      }
      case AttachmentTypes.Link: {
        const linkAttachment = attachment as LinkOrgaAttachmentDto;
        return [{
          attachmentId: linkAttachment.attachmentId,
          loadingId: this._getLoadingId(linkAttachment.attachmentId),
          tenantId: linkAttachment.tenantId,
          name: linkAttachment.name ?? undefined,
          url: linkAttachment.link ?? undefined,
          type: AttachmentTypes.Link
        }];
      }
      case AttachmentTypes.Knowledge: {
        const knowledgeAttachment = attachment as KnowledgeArticleOrgaAttachmentDto;
        return [{
          attachmentId: knowledgeAttachment.attachmentId,
          loadingId: this._getLoadingId(knowledgeAttachment.attachmentId),
          tenantId: knowledgeAttachment.tenantId,
          name: knowledgeAttachment.knowledgeArticle?.title ?? undefined,
          url: knowledgeAttachment.knowledgeArticle?.externalId ?? undefined,
          type: AttachmentTypes.Knowledge,
          referenceId: knowledgeAttachment.knowledgeArticle?.knowledgeArticleId
        }];
      }
      case AttachmentTypes.Folder: {
        const folderAttachment = attachment as FolderOrgaAttachmentDto;
        return [{
          attachmentId: folderAttachment.attachmentId,
          loadingId: this._getLoadingId(folderAttachment.attachmentId),
          tenantId: folderAttachment.tenantId,
          path: folderAttachment.path ?? undefined,
          type: AttachmentTypes.Folder
        }];
      }
      default:
        return [];
    }
  }

  private _mapOrgaListAttachment(attachment: OrgaAttachmentListDto): ILocalAttachmentDto[] {
    switch (attachment.type) {
      case AttachmentTypes.File:
        return [{
          attachmentId: attachment.attachmentId,
          loadingId: this._getLoadingId(attachment.attachmentId),
          tenantId: attachment.tenantId,
          name: attachment.name ?? undefined,
          url: attachment.url ?? attachment.link ?? undefined,
          type: AttachmentTypes.File,
          referenceId: attachment.referenceId
        }];
      case AttachmentTypes.Link:
        return [{
          attachmentId: attachment.attachmentId,
          loadingId: this._getLoadingId(attachment.attachmentId),
          tenantId: attachment.tenantId,
          name: attachment.name ?? undefined,
          url: attachment.link ?? attachment.url ?? undefined,
          type: AttachmentTypes.Link
        }];
      case AttachmentTypes.Knowledge:
        return [{
          attachmentId: attachment.attachmentId,
          loadingId: this._getLoadingId(attachment.attachmentId),
          tenantId: attachment.tenantId,
          name: attachment.name ?? undefined,
          url: attachment.url ?? undefined,
          type: AttachmentTypes.Knowledge,
          referenceId: attachment.referenceId
        }];
      case AttachmentTypes.Folder:
        return [{
          attachmentId: attachment.attachmentId,
          loadingId: this._getLoadingId(attachment.attachmentId),
          tenantId: attachment.tenantId,
          path: attachment.path ?? undefined,
          type: AttachmentTypes.Folder
        }];
      default:
        return [];
    }
  }

  private _getLoadingId(attachmentId?: number, fileId?: number): string | undefined {
    const id: number | string = attachmentId ?? fileId ?? crypto.randomUUID();

    if (fileId !== undefined) {
      return `file_${ fileId }`;
    }

    return `attachment_${ id }`;
  }

}
